/*
 * @Author: lizhigang
 * @Date: 2025-10-22 17:44:55
 * @Company: www.smm.cn
 * @Description: 忘记密码
 */
"use client"

import IconFont from "@repo/ui/iconfont"
import http from "@repo/ui/utils"
import md5 from "md5"
import { useLocale, useTranslations } from "next-intl"
import { useState } from "react"
import { Button, Col, Form, Input, message, Row, Space } from "antd"

import "./index.scss"

import { useFetchCode, useMessage, usePasswordInput } from "../../_hooks"
import { SIGN_IN_EN_H5_SOURCE_MAP, SIGN_IN_EN_WEB_SOURCE_MAP } from "../../const"
import { isMobile } from "../../utils"
import { AuthConfigProvider } from "../AuthConfigProvider"

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  schema?: "page" | "modal"
  language?: string
  onReseted?: (msg?: string) => void
  onClose?: () => void
}

const ForgotPassword = ({ schema = "modal", language, onReseted, onClose, ...attr }: Props) => {
  const locale = useLocale()
  const t = useTranslations("forgot")
  const [form] = Form.useForm()
  const [account, setAccount] = useState("")
  const [verifyCode, setVerifyCode] = useState("")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [messageApi, contextHolder] = message.useMessage({
    getContainer: () => document.querySelector("#action") || document.body,
  })

  // 使用 message hook，从 App.useApp() 获取 message 实例
  const { showSuccess, showError } = useMessage({ schema })

  const { fetchCode, count, sent } = useFetchCode({ account, form, action: "forgot" })

  // 使用密码输入 Hook - 新密码
  const { handlePasswordChange, handlePasswordPaste, handleCompositionStart } = usePasswordInput({
    form,
    fieldName: "password",
    onPasswordChange: setPassword,
  })

  // 使用密码输入 Hook - 确认密码
  const {
    handlePasswordChange: handlePassword2Change,
    handlePasswordPaste: handlePassword2Paste,
    handleCompositionStart: handlePassword2CompositionStart,
  } = usePasswordInput({
    form,
    fieldName: "password2",
  })

  /**
   * 重置密码
   */
  const handleSubmit = async () => {
    if (loading || !account.trim() || !password.trim() || !verifyCode.trim()) return
    const values = await form.validateFields()
    try {
      if (values) {
        const formData = new FormData()
        formData.append("user_name", values.user_name.trim())
        formData.append("password", md5(values.password))
        formData.append("verify_code", verifyCode.trim())
        formData.append(
          "source",
          isMobile()
            ? (SIGN_IN_EN_H5_SOURCE_MAP[window.location.host] ?? "")
            : (SIGN_IN_EN_WEB_SOURCE_MAP[window.location.host] ?? "")
        )
        formData.append("language", locale)
        setLoading(true)
        const response = await http.post<FormData, null>(`/usercenter/english/v1/reset_password`, {
          body: formData,
          showOriginResponse: true,
          getLang: () => locale,
        })
        setLoading(false)
        if (response?.code === 0) {
          schema === "modal"
            ? showSuccess(t("resetSuccess"))
            : messageApi.open({
                type: "success",
                content: t("resetSuccess"),
              })
          form.resetFields()
          // if (schema === "modal") {
          //   onClose?.()
          // }
          onReseted?.(t("resetSuccess"))
        } else {
          throw new Error(response?.msg)
        }
      }
    } catch (errorInfo) {
      const msg = typeof errorInfo === "string" ? errorInfo : (errorInfo as Error).message
      schema === "modal"
        ? showError(msg || "unknown error")
        : messageApi.open({
            type: "error",
            content: msg || "unknown error",
          })
    }
  }

  return (
    <AuthConfigProvider>
      {contextHolder}
      <div className={`forgotWrapper ${schema === "page" ? "page" : "modal"}`} {...attr}>
        <div className="logo">
          <IconFont type="icon-SMMlogo-daizimu" style={{ width: 80, height: 32, fontSize: 80 }} />
        </div>
        <h2 className={`modalTitle ${schema === "page" ? "page" : "modal"}`}>{t("title")}</h2>
        {/*<ConfigProvider*/}
        {/*  theme={{*/}
        {/*    components: {*/}
        {/*      Form: {*/}
        {/*        verticalLabelMargin: 40,*/}
        {/*      },*/}
        {/*    },*/}
        {/*  }}*/}
        {/*>*/}
        <Form
          layout="vertical"
          form={form}
          variant="filled"
          initialValues={{ variant: "borderless" }}
          className={"forgotForm"}
          requiredMark={false}
        >
          <Form.Item
            name="user_name"
            label={schema === "page" ? null : t("emailAddress")}
            normalize={(value) => value?.trim()}
            rules={[
              { required: true, message: t("requireEmail") },
              { type: "email", message: t("validEmail") },
            ]}
          >
            <Input
              placeholder={t("emailAddress")}
              onChange={(e) => setAccount(e.target.value)}
              onPressEnter={fetchCode}
              allowClear
              autoFocus
            />
          </Form.Item>
          <Form.Item
            name="verify_code"
            label={schema === "page" ? null : t("emailCode")}
            normalize={(value) => value?.trim()}
            rules={[{ required: true, message: t("requireCode") }]}
          >
            <Space.Compact block>
              <Input
                placeholder={t("emailCode")}
                onChange={(e) => setVerifyCode(e.target.value)}
                onPressEnter={handleSubmit}
                allowClear
              />
              <span className="sendButton">
                <Button disabled={!account || (count > 0 && count < 60)} onClick={fetchCode}>
                  {count > 0 && count < 60 ? `${t("resend")}(${count}${t("s")})` : sent ? t("resend") : t("send")}
                </Button>
              </span>
            </Space.Compact>
          </Form.Item>
          <Row>
            <Col className="passwordWrapper">
              <Form.Item
                name="password"
                className="password"
                label={schema === "page" ? null : t("newPassword")}
                normalize={(value) => value?.trim()}
                rules={[
                  { required: true, message: t("requirePassword") },
                  {
                    min: 6,
                    message: t("newPassword_error1"),
                  },
                ]}
                // extra={schema === "modal" && <span className="modalSubTip">{t("characters6")}</span>}
              >
                <Input.Password
                  placeholder={t("newPassword")}
                  onChange={handlePasswordChange}
                  onPressEnter={handleSubmit}
                  onCompositionStart={handleCompositionStart}
                  onPaste={handlePasswordPaste}
                  allowClear
                />
              </Form.Item>
              {schema === "modal" && <span className="modalSubTip">{t("characters6")}</span>}
            </Col>
          </Row>
          <Form.Item
            name="password2"
            className="password2"
            dependencies={["password"]}
            label={schema === "page" ? null : t("confirmPassword")}
            normalize={(value) => value?.trim()}
            rules={[
              { required: true, message: t("requireConfirmPassword") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error(t("newPassword_error2")))
                },
              }),
            ]}
          >
            <Input.Password
              placeholder={t("confirmPassword")}
              onChange={handlePassword2Change}
              onPressEnter={handleSubmit}
              onCompositionStart={handlePassword2CompositionStart}
              onPaste={handlePassword2Paste}
              allowClear
            />
          </Form.Item>
          <Form.Item className="submitButton">
            <Button
              type="primary"
              onClick={handleSubmit}
              disabled={!account.trim() || !password.trim() || !verifyCode.trim()}
              loading={loading}
              block
              danger
            >
              {t("reset")}
            </Button>
          </Form.Item>
        </Form>
        {/*</ConfigProvider>*/}
      </div>
    </AuthConfigProvider>
  )
}

export default ForgotPassword
